<script setup>
import { computed, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

import Button from 'dashboard/components-next/button/Button.vue';
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import StartStep from 'dashboard/components-next/captain/pageComponents/assistant/create/StartStep.vue';
import RuleListEditor from 'dashboard/components-next/captain/pageComponents/assistant/create/RuleListEditor.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { shouldShowPaywall } = usePolicy();
const { isCloudFeatureEnabled } = useAccount();

const showPaywall = computed(() => shouldShowPaywall(FEATURE_FLAGS.CAPTAIN));
// Guidelines, guardrails, and scenarios only surface in the product on
// Captain V2 accounts, so don't seed content a v1 account can never reach.
const hasCaptainV2 = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CAPTAIN_V2)
);

const STEP_COUNT = 3;
const step = ref(1);

const state = reactive({
  name: '',
  description: '',
  productName: '',
  guidelines: [],
  guardrails: [],
  scenarios: [],
  inboxId: null,
});

const isCreating = ref(false);

const validationRules = {
  name: { required },
  description: { required },
  productName: { required },
};

const v$ = useVuelidate(validationRules, state);

const steps = computed(() => [
  t('CAPTAIN.ASSISTANTS.CREATE_FLOW.STEPS.START'),
  t('CAPTAIN.ASSISTANTS.CREATE_FLOW.STEPS.DETAILS'),
  t('CAPTAIN.ASSISTANTS.CREATE_FLOW.STEPS.CHANNEL'),
]);

const stageClass = index => {
  if (index + 1 < step.value) return 'bg-n-teal-3 text-n-teal-11';
  if (index + 1 === step.value) return 'bg-n-violet-9 text-white';
  return 'bg-n-slate-3 text-n-slate-11';
};

const inboxes = useMapGetter('inboxes/getInboxes');
const inboxOptions = computed(() =>
  inboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const getErrorMessage = (field, errorKey) =>
  v$.value[field].$error ? t(`CAPTAIN.ASSISTANTS.FORM.${errorKey}.ERROR`) : '';

const formErrors = computed(() => ({
  name: getErrorMessage('name', 'NAME'),
  description: getErrorMessage('description', 'DESCRIPTION'),
  productName: getErrorMessage('productName', 'PRODUCT_NAME'),
}));

const applyPrefill = prefill => {
  Object.assign(state, {
    name: prefill.name ?? state.name,
    description: prefill.description ?? state.description,
    productName: prefill.productName ?? state.productName,
    guidelines: hasCaptainV2.value ? (prefill.guidelines ?? []) : [],
    guardrails: hasCaptainV2.value ? (prefill.guardrails ?? []) : [],
    scenarios: hasCaptainV2.value ? (prefill.scenarios ?? []) : [],
  });
  v$.value.$reset();
  step.value = 2;
};

const handleTemplateSelect = template => {
  applyPrefill({
    name: '',
    description: template.agentDescription,
    productName: '',
    guidelines: [...template.guidelines],
    guardrails: [...template.guardrails],
    scenarios: template.scenarios.map(scenario => ({ ...scenario })),
  });
};

const removeScenario = index => {
  state.scenarios = state.scenarios.filter((_, i) => i !== index);
};

const goToChannelStep = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  step.value = 3;
};

const buildAssistantPayload = () => {
  const payload = {
    name: state.name,
    description: state.description,
    config: { product_name: state.productName },
  };
  if (state.guidelines.length) payload.response_guidelines = state.guidelines;
  if (state.guardrails.length) payload.guardrails = state.guardrails;
  return payload;
};

const seedAssistant = async assistantId => {
  const requests = state.scenarios.map(scenario =>
    store.dispatch('captainScenarios/create', {
      assistantId,
      title: scenario.title,
      description: scenario.description,
      instruction: scenario.instruction,
      enabled: true,
    })
  );
  if (state.inboxId) {
    requests.push(
      store.dispatch('captainInboxes/create', {
        assistantId,
        inboxIds: [state.inboxId],
      })
    );
  }

  const results = await Promise.allSettled(requests);
  return results.every(result => result.status === 'fulfilled');
};

const createAgent = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    step.value = 2;
    return;
  }

  isCreating.value = true;
  let assistant;
  try {
    assistant = await store.dispatch(
      'captainAssistants/create',
      buildAssistantPayload()
    );
  } catch (error) {
    useAlert(error?.message || t('CAPTAIN.ASSISTANTS.CREATE.ERROR_MESSAGE'));
    isCreating.value = false;
    return;
  }

  const seeded = await seedAssistant(assistant.id);
  useAlert(
    seeded
      ? t('CAPTAIN.ASSISTANTS.CREATE_FLOW.SUCCESS_MESSAGE')
      : t('CAPTAIN.ASSISTANTS.CREATE_FLOW.PARTIAL_ERROR_MESSAGE')
  );
  router.push({
    name: 'captain_assistants_overview_index',
    params: {
      accountId: route.params.accountId,
      assistantId: assistant.id,
    },
  });
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-y-auto bg-n-surface-1">
    <div v-if="showPaywall" class="w-full max-w-5xl px-6 py-8 mx-auto">
      <CaptainPaywall />
    </div>
    <div v-else class="flex flex-col w-full max-w-3xl gap-6 px-6 py-8 mx-auto">
      <header class="flex flex-col gap-5">
        <div class="flex items-center gap-3">
          <BackButton :back-url="{ name: 'captain_assistants_create_index' }" />
          <h1 class="mb-0 text-xl font-semibold tracking-tight text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.CREATE_FLOW.TITLE') }}
          </h1>
          <span class="text-sm text-n-slate-11">
            {{
              t('CAPTAIN.ASSISTANTS.CREATE_FLOW.STEP_LABEL', {
                step,
                total: STEP_COUNT,
              })
            }}
          </span>
        </div>
        <ol class="flex flex-wrap items-center gap-2 p-0 m-0 list-none">
          <li
            v-for="(stage, index) in steps"
            :key="stage"
            class="flex items-center gap-2"
            :aria-current="index + 1 === step ? 'step' : undefined"
          >
            <span
              class="grid text-xs font-semibold rounded-full size-6 place-items-center"
              :class="stageClass(index)"
            >
              <span v-if="index + 1 < step" class="i-lucide-check size-3.5" />
              <template v-else>{{ index + 1 }}</template>
            </span>
            <span
              class="text-sm"
              :class="
                index + 1 === step
                  ? 'font-medium text-n-slate-12'
                  : 'text-n-slate-11'
              "
            >
              {{ stage }}
            </span>
            <span
              v-if="index < steps.length - 1"
              class="i-lucide-chevron-right size-4 text-n-slate-9"
            />
          </li>
        </ol>
      </header>

      <StartStep
        v-if="step === 1"
        :show-behavior="hasCaptainV2"
        @select="handleTemplateSelect"
        @drafted="applyPrefill"
      />

      <div v-else-if="step === 2" class="flex flex-col gap-8">
        <div class="flex flex-col gap-4">
          <h3 class="mb-0 text-base font-semibold text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.CREATE_FLOW.DETAILS.IDENTITY_TITLE') }}
          </h3>
          <Input
            v-model="state.name"
            :label="t('CAPTAIN.ASSISTANTS.FORM.NAME.LABEL')"
            :placeholder="t('CAPTAIN.ASSISTANTS.FORM.NAME.PLACEHOLDER')"
            :message="formErrors.name"
            :message-type="formErrors.name ? 'error' : 'info'"
          />
          <TextArea
            v-model="state.description"
            auto-height
            show-character-count
            :max-length="500"
            :label="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.LABEL')"
            :placeholder="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.PLACEHOLDER')"
            :message="formErrors.description"
            :message-type="formErrors.description ? 'error' : 'info'"
          />
          <Input
            v-model="state.productName"
            :label="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.LABEL')"
            :placeholder="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.PLACEHOLDER')"
            :message="formErrors.productName"
            :message-type="formErrors.productName ? 'error' : 'info'"
          />
        </div>

        <div v-if="hasCaptainV2" class="flex flex-col gap-4">
          <div>
            <h3 class="mb-0 text-base font-semibold text-n-slate-12">
              {{ t('CAPTAIN.ASSISTANTS.CREATE_FLOW.DETAILS.BEHAVIOR_TITLE') }}
            </h3>
            <p class="mb-0 mt-1 text-sm text-n-slate-11">
              {{
                t('CAPTAIN.ASSISTANTS.CREATE_FLOW.DETAILS.BEHAVIOR_SUBTITLE')
              }}
            </p>
          </div>
          <RuleListEditor
            v-model="state.guidelines"
            :label="
              t('CAPTAIN.ASSISTANTS.CREATE_FLOW.DETAILS.GUIDELINES_LABEL')
            "
            :placeholder="
              t('CAPTAIN.ASSISTANTS.CREATE_FLOW.DETAILS.GUIDELINES_PLACEHOLDER')
            "
          />
          <RuleListEditor
            v-model="state.guardrails"
            :label="
              t('CAPTAIN.ASSISTANTS.CREATE_FLOW.DETAILS.GUARDRAILS_LABEL')
            "
            :placeholder="
              t('CAPTAIN.ASSISTANTS.CREATE_FLOW.DETAILS.GUARDRAILS_PLACEHOLDER')
            "
          />
          <div v-if="state.scenarios.length" class="flex flex-col gap-2">
            <div>
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  t('CAPTAIN.ASSISTANTS.CREATE_FLOW.DETAILS.SCENARIOS_LABEL')
                }}
              </span>
              <p class="mb-0 mt-1 text-sm text-n-slate-11">
                {{
                  t('CAPTAIN.ASSISTANTS.CREATE_FLOW.DETAILS.SCENARIOS_SUBTITLE')
                }}
              </p>
            </div>
            <ul class="flex flex-col gap-2 pl-0 mb-0 list-none">
              <li
                v-for="(scenario, index) in state.scenarios"
                :key="`${index}-${scenario.title}`"
                class="flex items-start justify-between gap-3 px-3 py-2 border rounded-lg border-n-weak bg-n-solid-1"
              >
                <div class="min-w-0">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ scenario.title }}
                  </span>
                  <p class="mb-0 text-sm text-n-slate-11">
                    {{ scenario.description }}
                  </p>
                </div>
                <Button
                  ghost
                  slate
                  xs
                  type="button"
                  icon="i-lucide-x"
                  :aria-label="
                    t('CAPTAIN.ASSISTANTS.CREATE_FLOW.DETAILS.REMOVE')
                  "
                  @click="removeScenario(index)"
                />
              </li>
            </ul>
          </div>
        </div>

        <div
          class="flex items-center justify-between pt-5 border-t border-n-weak"
        >
          <Button
            faded
            slate
            type="button"
            :label="t('CAPTAIN.ASSISTANTS.CREATE_FLOW.PREVIOUS')"
            @click="step = 1"
          />
          <Button
            type="button"
            trailing-icon
            icon="i-lucide-arrow-right"
            :label="t('CAPTAIN.ASSISTANTS.CREATE_FLOW.CONTINUE')"
            @click="goToChannelStep"
          />
        </div>
      </div>

      <div v-else class="flex flex-col gap-6">
        <div>
          <h3 class="mb-0 text-base font-semibold text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.CREATE_FLOW.CHANNEL.TITLE') }}
          </h3>
          <p class="mb-0 mt-1 text-sm text-n-slate-11">
            {{ t('CAPTAIN.ASSISTANTS.CREATE_FLOW.CHANNEL.SUBTITLE') }}
          </p>
        </div>
        <div class="flex flex-col gap-1">
          <label
            for="create-flow-inbox"
            class="mb-0.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CAPTAIN.ASSISTANTS.CREATE_FLOW.CHANNEL.LABEL') }}
          </label>
          <ComboBox
            id="create-flow-inbox"
            v-model="state.inboxId"
            :options="inboxOptions"
            :placeholder="
              t('CAPTAIN.ASSISTANTS.CREATE_FLOW.CHANNEL.PLACEHOLDER')
            "
          />
        </div>

        <div
          class="flex items-center justify-between pt-5 border-t border-n-weak"
        >
          <Button
            faded
            slate
            type="button"
            :label="t('CAPTAIN.ASSISTANTS.CREATE_FLOW.PREVIOUS')"
            :disabled="isCreating"
            @click="step = 2"
          />
          <Button
            type="button"
            icon="i-lucide-sparkles"
            :label="t('CAPTAIN.ASSISTANTS.CREATE_FLOW.CREATE')"
            :is-loading="isCreating"
            :disabled="isCreating"
            @click="createAgent"
          />
        </div>
      </div>
    </div>
  </section>
</template>
